import React, { useEffect, useState, useRef } from 'react';
import {
  PhoneXMarkIcon,
  PlusIcon,
  PhoneIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import InputMask from 'react-input-mask';
import ModalDelete from '../../components/modal-delete/modal-delete';
import blockedNumberApi from '../../services/blockedNumberApi';
import { SkeletonBlock } from '../../components/skeleton/skeleton';
import {
  PageHeader,
  TWButton,
  TWModal,
  TWDataTable,
  FieldLabel,
  EmptyState,
  IconBadge,
} from '../../components/ui';
import { headerCls, DeleteAction } from '../../data/columns';

const BlockedNumbers = () => {
  const [blockedNumberList, setBlockedNumberList] = useState([]);
  const [deletedNumber, setDeletedNumber] = useState({});
  const [loading, setLoading] = useState({
    listLoading: true,
    upsertLoading: false,
    deleteLoading: false,
  });
  const [form, setForm] = useState({ number: '' });
  const [dialogs, setDialogs] = useState({ upsert: false, delete: false });
  const [errors, setErrors] = useState({ number: false });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: 0,
  });
  const cancelButtonRef = useRef(null);

  const getBlockedNumberList = async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    setBlockedNumberList([]);
    try {
      const query = `?PageNumber=${pagination.pageNumber}&PageSize=${pagination.pageSize}`;
      const list = await blockedNumberApi.list(query);
      if (list.data.data?.length > 0) {
        setBlockedNumberList(list.data.data);
        setPagination({
          pageNumber: list.data.pageNumber,
          pageSize: list.data.pageSize,
          totalRecords: list.data.totalRecords,
        });
      }
    } catch (error) {
      // silent
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  useEffect(() => {
    getBlockedNumberList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageNumber]);

  const onClickAdd = () => {
    setForm({ number: '' });
    setDialogs((prev) => ({ ...prev, upsert: true }));
  };

  const onFormSubmit = async () => {
    if (!form.number) {
      setErrors({ number: true });
      return;
    }
    setErrors({ number: false });
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const cleanedNumber = '+1' + form.number.replace(/\D/g, '');
      await blockedNumberApi.create({ number: cleanedNumber });
    } catch (error) {
      // silent
    } finally {
      await getBlockedNumberList();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, upsert: false }));
    }
  };

  const onCloseUpsert = () => {
    setErrors({ number: false });
    setDialogs((prev) => ({ ...prev, upsert: false }));
  };

  const onClickDelete = (number) => {
    setDeletedNumber({ name: number.number, id: number.id });
    setDialogs((prev) => ({ ...prev, delete: true }));
  };

  const onConfirmDelete = async () => {
    setLoading((prev) => ({ ...prev, deleteLoading: true }));
    try {
      await blockedNumberApi.deleteById(deletedNumber.id);
    } catch (error) {
      // silent
    } finally {
      setDialogs((prev) => ({ ...prev, delete: false, upsert: false }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
      await getBlockedNumberList();
    }
  };

  const BlockedNumbersSkeleton = () => (
    <div className="reveal" aria-hidden="true">
      <table className="min-w-full divide-y divide-gray-300 dark:divide-slate-500">
        <thead>
          <tr>
            <th className="py-3.5 pr-3 text-left text-sm font-semibold text-slate-900 dark:text-slate-100">
              Phone Number
            </th>
            <th className="py-3.5 px-4 text-right">
              <span className="sr-only">Delete</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 dark:divide-slate-500">
          {[...Array(6)].map((_, i) => (
            <tr key={i}>
              <td className="whitespace-nowrap py-4 pr-3">
                <div className="flex items-center gap-3">
                  <SkeletonBlock className="h-9 w-9 !rounded-md" />
                  <SkeletonBlock className="h-4 w-40" />
                </div>
              </td>
              <td className="whitespace-nowrap py-4 px-4 text-right">
                <SkeletonBlock className="ml-auto h-8 w-8 !rounded-full" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const columns = [
    {
      title: <span className={headerCls}>Phone Number</span>,
      key: 'number',
      render: (_, item) => (
        <div className="flex items-center gap-3">
          <IconBadge icon={PhoneXMarkIcon} tone="rose" size="sm" />
          <span className="font-mono text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
            {item.number}
          </span>
        </div>
      ),
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      align: 'right',
      width: 60,
      render: (_, item) => <DeleteAction onClick={() => onClickDelete(item)} />,
    },
  ];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Blocked Numbers"
          description="Phone numbers on this list will be prevented from placing orders."
          actions={
            <TWButton
              variant="primary"
              icon={PlusIcon}
              label="Add Number"
              onClick={onClickAdd}
            />
          }
        />

        {loading.listLoading ? (
          <BlockedNumbersSkeleton />
        ) : blockedNumberList.length === 0 ? (
          <EmptyState
            icon={PhoneXMarkIcon}
            title="No blocked numbers"
            description="Add a number to prevent it from ordering."
          />
        ) : (
          <div className="reveal">
            <TWDataTable
              columns={columns}
              data={blockedNumberList}
              rowKey="id"
              pagination={
                pagination.totalRecords > pagination.pageSize
                  ? {
                      current: pagination.pageNumber,
                      pageSize: pagination.pageSize,
                      total: pagination.totalRecords,
                      showTotal: (total, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${total} blocked numbers`,
                      onChange: (page) =>
                        setPagination((prev) => ({
                          ...prev,
                          pageNumber: page,
                        })),
                      showSizeChanger: false,
                    }
                  : undefined
              }
            />
          </div>
        )}
      </div>

      <TWModal
        open={dialogs.upsert}
        onClose={onCloseUpsert}
        title="Block Phone Number"
        description="This number will be blocked from ordering"
        size="md"
        initialFocus={cancelButtonRef}
        showCloseButton
        footer={
          <div className="flex justify-end gap-2">
            <TWButton
              ref={cancelButtonRef}
              label="Cancel"
              disabled={loading.upsertLoading}
              onClick={onCloseUpsert}
            />
            <TWButton
              variant="danger"
              icon={NoSymbolIcon}
              label="Block Number"
              loading={loading.upsertLoading}
              disabled={loading.upsertLoading}
              onClick={onFormSubmit}
            />
          </div>
        }
      >
        <div>
          <FieldLabel htmlFor="number" required>
            Phone Number
          </FieldLabel>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
              <PhoneIcon className="h-4 w-4" />
            </span>
            <InputMask
              value={form.number}
              mask="(999) 999 9999"
              maskPlaceholder=" "
              onChange={(e) =>
                setForm((prev) => ({ ...prev, number: e.target.value }))
              }
              type="tel"
              name="number"
              id="number"
              className="block w-full rounded-md border border-slate-200 bg-white py-2 pl-10 pr-3 font-mono text-sm text-slate-900 placeholder-slate-400 shadow-sm transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-sky-400 dark:focus:ring-sky-400"
              placeholder="(555) 123 4567"
            />
          </div>
          {errors.number && (
            <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-400">
              Phone number cannot be empty
            </p>
          )}
        </div>
      </TWModal>

      <ModalDelete
        title="Delete Blocked Number"
        loading={loading.deleteLoading}
        open={dialogs.delete}
        onCloseRequest={() =>
          setDialogs((prev) => ({ ...prev, delete: false }))
        }
        onOk={onConfirmDelete}
        item={deletedNumber}
      />
    </div>
  );
};

export default BlockedNumbers;
